import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { authActions } from '../../_store';
import styles from './passwordReset.module.css';
import SuccessMessage from '../../_components/successMessage/successMessage';
import {
    validatePassword,
    validatePasswordMatch,
} from '../../_helpers/validate';
import InputSuccessMessage from '../../_components/inputMessages/inputSuccessMessage/inputSuccessMessage';
import InputErrorMessage from '../../_components/inputMessages/inputErrorMessage/inputErrorMessage';
import PasswordStrengthChecker from '../../_components/passwordStrengthChecker/passwordStrengthChecker';
import PasswordValidator from '../../_components/passwordValidator/passwordValidator';
import ErrorMessage from '../../_components/errorMessage/errorMessage';

export { PasswordReset };

function PasswordReset() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    // STATE
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [passStrData, setPassStrData] = useState(null);
    const [userPassword, setUserPassword] = useState(undefined);
    const [validPasswordMatch, setValidPasswordMatch] = useState(null);
    const [passLengthErr, setPassLengthErr] = useState(null);
    const [userPasswordConfirm, setUserPasswordConfirm] = useState(undefined);
    const [errorLogin, setErrorLogin] = useState(null);

    // Initial setup for navigation props
    dispatch(
        authActions.setNavProps({
            title: 'LOGIN TO SPLENDI',
            button: 'Register',
            action: '../register',
            status: "Don't have an account?",
        })
    );

    // UseEffects
    useEffect(() => {
        setShowSuccess(false);
        setUserPassword(undefined);
        setPassStrData(null);
        setErrorLogin(null);
    }, []);

    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters')
            .matches(
                /(?=.*[a-z])(?=.*[A-Z])|(?=.*[0-9])|(?=.*[!@#$%^&*])/,
                'Password must contain at least two of the following: uppercase letters, lowercase letters, numbers, special characters'
            ),
        confirmPassword: Yup.string().oneOf(
            [Yup.ref('password'), null],
            'Passwords must match'
        ),
    });

    const { register, handleSubmit, formState, clearErrors } = useForm({
        resolver: yupResolver(validationSchema),
    });
    const { errors, isSubmitting } = formState;

    const onSubmit = async ({ password }) => {
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        const id = searchParams.get('id');
        const migrated = searchParams.get('migrated');
        setShowSuccess(false);
        setErrorLogin(null);

        if (passStrData && passStrData.contains.length !== 4) {
            setPassLengthErr(true);
            return null;
        }

        setPassLengthErr(false);
        if (token && type && id) {
            const response = await dispatch(
                authActions.passwordReset({ password, token, id })
            ).unwrap();

            if (response && response.statusCode === 400) {
                setErrorLogin(response.message);
            } else {
                setShowSuccess(true);
                setErrorLogin(null);
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        }
        if (id && migrated && migrated === 'true') {
            const response = await dispatch(
                authActions.passwordResetMigratedUser({ password, id })
            ).unwrap();
            if (response && response.statusCode === 400) {
                setErrorLogin(response.message);
            } else {
                setShowSuccess(true);
                setErrorLogin(null);
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        }
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    const validateInputs = (e) => {
        const { name, value } = e.target;
        let valid;

        switch (name) {
            case 'password':
                const strengthCheckerData = validatePassword(value);
                setPassStrData(strengthCheckerData);
                setUserPassword(value);

                if (
                    userPasswordConfirm !== null &&
                    userPasswordConfirm !== undefined &&
                    userPasswordConfirm !== ''
                ) {
                    valid = validatePasswordMatch(userPasswordConfirm, value);
                    valid
                        ? setValidPasswordMatch(true)
                        : setValidPasswordMatch(false);
                }
                break;
            case 'confirmPassword':
                valid = validatePasswordMatch(userPassword, value);
                setValidPasswordMatch(valid);
                setUserPasswordConfirm(value);
                break;
            default:
                break;
        }
    };

    const renderTemplate = () => {
        dispatch(
            authActions.setNavProps({
                title: 'LOGIN TO SPLENDI',
                button: 'Login',
                action: '../login',
                status: 'Already signed up?',
            })
        );
        return (
            <div className='card-body'>
                <h2 className='card-header no-border text-center mb-4'>
                    Change your password
                </h2>
                {showSuccess ? (
                    <div className='mb-3 ml-6'>
                        <SuccessMessage message='YOUR PASSWORD HAS BEEN CHANGED SUCCESSFULLY' />
                    </div>
                ) : (
                    <p className={`ml-6 ${styles.alertMessage}`}>
                        <FiAlertTriangle /> Please enter new password
                    </p>
                )}
                {/* error message */}
                {errorLogin ? (
                    <div className='mt-3 mb-3 ml-6'>
                        <ErrorMessage message={errorLogin} />
                    </div>
                ) : null}
                <form onSubmit={handleSubmit(onSubmit)} className='mt-4'>
                    <div className='mb-3 ml-6'>
                        <label className='form-label fw-bold'>Password</label>
                        <div className='password-input-container'>
                            <input
                                disabled={showSuccess}
                                name='password'
                                type={passwordVisible ? 'text' : 'password'}
                                {...register('password')}
                                className='form-control'
                                onChange={(e) => {
                                    clearErrors('password');
                                    setPassLengthErr(false);
                                    if (e.target.value.includes(' ')) {
                                        return null;
                                    } else {
                                        validateInputs(e);
                                    }
                                }}
                                value={userPassword}
                            />
                            <span
                                className='password-toggle-icon'
                                onClick={togglePasswordVisibility}
                            >
                                {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                    </div>
                    <div className='mb-3 ml-6'>
                        <label className='form-label fw-bold'>
                            Confirm Password
                        </label>
                        <div className='password-input-container'>
                            <input
                                name='confirmPassword'
                                type={
                                    confirmPasswordVisible ? 'text' : 'password'
                                }
                                {...register('confirmPassword')}
                                className={`form-control ${
                                    errors.confirmPassword ? 'is-invalid' : ''
                                }`}
                                onChange={(e) => {
                                    clearErrors('confirmPassword');
                                    if (e.target.value.includes(' ')) {
                                        return null;
                                    } else {
                                        validateInputs(e);
                                    }
                                }}
                                value={userPasswordConfirm}
                            />
                            <span
                                className='password-toggle-icon'
                                onClick={toggleConfirmPasswordVisibility}
                            >
                                {confirmPasswordVisible ? (
                                    <FaEyeSlash />
                                ) : (
                                    <FaEye />
                                )}
                            </span>
                        </div>
                        <div className='mt-3'>
                            {validPasswordMatch === true ? (
                                <InputSuccessMessage
                                    message={'The passwords are matching!'}
                                />
                            ) : null}
                            {validPasswordMatch === false ? (
                                <InputErrorMessage
                                    message={'The passwords do not match!'}
                                />
                            ) : null}
                            {passLengthErr === true ? (
                                <InputErrorMessage
                                    message={
                                        'Password has not fulfilled the requirements!'
                                    }
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className='mb-3 ml-6 mt-4 mb-5'>
                        {/* Password Strength Checker */}
                        <PasswordStrengthChecker passwordData={passStrData} />
                    </div>
                    <div className='mb-3 ml-6 mt-4 mb-5'>
                        {/* Password Validator */}
                        <PasswordValidator passwordData={passStrData} />
                    </div>
                    <div className='d-grid gap-2 ml-6'>
                        <button
                            disabled={isSubmitting}
                            className='btn btn-black mb-2 w-100'
                        >
                            {isSubmitting && (
                                <span className='spinner-border spinner-border-sm me-1'></span>
                            )}
                            SUBMIT
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    return (
        <div className='card no-border m-md-3 pt-4 pb-5 d-flex align-items-center'>
            {renderTemplate()}
        </div>
    );
}
